import { computed, inject, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';

import { FundConfig } from '@demeter/lib/models/config';
import { CoreConfigApiService } from '@demeter/modules/api/core-config-api.service';

import { UserSettingsService } from './user-settings.service';

export type FlagsState = 'loading' | 'ready' | 'unavailable';

// The expansion seam: the funds list consumes isFundVisible() today; fund pickers and
// transactions adopt the same predicate later. Flags come from Core.Config's DIRECT url —
// never Core.API's APIM-cached GET /v1/funds (300 s stale curation).
@Injectable({ providedIn: 'root' })
export class PresentationModeService {
	private readonly configApi = inject(CoreConfigApiService);
	private readonly userSettings = inject(UserSettingsService);

	private readonly _flags = signal<FundConfig[]>([]);
	private readonly _state = signal<FlagsState>('loading');

	readonly state = this._state.asReadonly();
	readonly enabled = computed(() => this.userSettings.settings().presentationMode);
	readonly visibleFundIds = computed(
		() =>
			new Set(
				this._flags()
					.filter(f => f.showInPresentation ?? false)
					.map(f => f.fundId)
			)
	);
	readonly counts = computed(() => ({
		shown: this.enabled() ? this.visibleFundIds().size : this._flags().length,
		total: this._flags().length
	}));

	load(): void {
		this._state.set('loading');
		this.configApi
			.getFunds()
			.pipe(take(1))
			.subscribe({
				next: flags => {
					this._flags.set(flags);
					this._state.set('ready');
				},
				// Display-only filter: a flags failure must never blank the funds page or
				// masquerade as "zero flagged". Fail OPEN.
				error: () => this._state.set('unavailable')
			});
	}

	isFundVisible(fundId: string): boolean {
		if (!this.enabled() || this._state() !== 'ready') {
			return true;
		}
		return this.visibleFundIds().has(fundId);
	}
}
